import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Minus, Users, ChevronDown } from 'lucide-react';

/**
 * ConditionalField - Renders conditional custom fields
 * 
 * Two types:
 * 1. conditional_number: Shows a count input, then generates that many sets of sub-fields
 * 2. conditional_dropdown: Shows a dropdown, then shows different sub-fields based on selection
 */
const ConditionalField = ({ field, value, onChange }) => {
  const config = field.conditionalConfig || {};
  const fieldType = field.type;

  if (fieldType === 'conditional_number') {
    return <ConditionalNumberField field={field} config={config} value={value} onChange={onChange} />;
  } else if (fieldType === 'conditional_dropdown') {
    return <ConditionalDropdownField field={field} config={config} value={value} onChange={onChange} />;
  }

  return null;
};

/**
 * ConditionalNumberField - Number-triggered conditional field
 * Example: "Children" field - enter 2, get 2 sets of (Name, DOB, SIN) inputs
 */
const ConditionalNumberField = ({ field, config, value, onChange }) => {
  const maxCount = config.maxCount || 10;
  const subFields = config.subFields || [];
  
  // Parse existing value or initialize
  const [count, setCount] = useState(() => {
    if (value && typeof value === 'object') {
      return value.count || 0;
    }
    return 0;
  });
  
  const [items, setItems] = useState(() => {
    if (value && typeof value === 'object' && Array.isArray(value.items)) {
      return value.items;
    }
    return [];
  });

  // Update parent when count or items change
  useEffect(() => {
    onChange({
      count,
      items: items.slice(0, count) // Only include items up to count
    });
  }, [count, items]);

  const handleCountChange = (newCount) => {
    const validCount = Math.max(0, Math.min(maxCount, parseInt(newCount) || 0));
    setCount(validCount);
    
    // Expand items array if needed
    if (validCount > items.length) {
      const newItems = [...items];
      for (let i = items.length; i < validCount; i++) {
        const emptyItem = {};
        subFields.forEach(sf => {
          emptyItem[sf.id] = '';
        });
        newItems.push(emptyItem);
      }
      setItems(newItems);
    }
  };

  const handleItemFieldChange = (itemIndex, fieldId, fieldValue) => {
    const newItems = [...items];
    if (!newItems[itemIndex]) {
      newItems[itemIndex] = {};
    }
    newItems[itemIndex][fieldId] = fieldValue;
    setItems(newItems);
  };

  const incrementCount = () => {
    if (count < maxCount) {
      handleCountChange(count + 1);
    }
  };

  const decrementCount = () => {
    if (count > 0) {
      handleCountChange(count - 1);
    }
  };

  const renderSubFieldInput = (subField, itemIndex, itemData) => {
    const val = itemData?.[subField.id] || '';
    
    switch (subField.type) {
      case 'date':
        return (
          <Input
            type="date"
            value={val}
            onChange={(e) => handleItemFieldChange(itemIndex, subField.id, e.target.value)}
            className="flex-1"
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={val}
            onChange={(e) => handleItemFieldChange(itemIndex, subField.id, e.target.value)}
            className="flex-1"
          />
        );
      case 'checkbox':
        return (
          <Checkbox
            checked={val === true || val === 'true'}
            onCheckedChange={(checked) => handleItemFieldChange(itemIndex, subField.id, checked)}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={val}
            onChange={(e) => handleItemFieldChange(itemIndex, subField.id, e.target.value)}
            placeholder={subField.name}
            className="flex-1"
          />
        );
    }
  };

  return (
    <div className="space-y-3">
      {/* Count input with increment/decrement buttons */}
      <div className="flex items-center gap-3">
        <Label className="text-sm font-medium min-w-fit">
          How many {field.name.toLowerCase()}?
        </Label>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={decrementCount}
            disabled={count <= 0}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Input
            type="number"
            min="0"
            max={maxCount}
            value={count}
            onChange={(e) => handleCountChange(e.target.value)}
            className="w-16 text-center"
            data-testid={`conditional-count-${field.fieldId}`}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={incrementCount}
            disabled={count >= maxCount}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <span className="text-xs text-gray-500">(max {maxCount})</span>
      </div>

      {/* Dynamic item cards */}
      {count > 0 && (
        <div className="space-y-3 mt-4">
          {Array.from({ length: count }).map((_, itemIndex) => (
            <Card key={itemIndex} className="bg-gray-50 border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-blue-700">{itemIndex + 1}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {field.name} #{itemIndex + 1}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {subFields.map((subField) => (
                    <div key={subField.id} className="space-y-1">
                      <Label className="text-xs text-gray-600">
                        {subField.name}
                        {subField.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {renderSubFieldInput(subField, itemIndex, items[itemIndex])}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {count === 0 && (
        <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed">
          <Users className="h-8 w-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            Enter a number above to add {field.name.toLowerCase()}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * ConditionalDropdownField - Dropdown-triggered conditional field
 * Example: "Employment Status" - select "Employed" to see Employer Name, Position fields
 */
const ConditionalDropdownField = ({ field, config, value, onChange }) => {
  const dropdownOptions = config.dropdownOptions || [];
  const conditions = config.conditions || {};
  
  // Parse existing value or initialize
  const [selectedOption, setSelectedOption] = useState(() => {
    if (value && typeof value === 'object') {
      return value.selectedOption || '';
    }
    return '';
  });
  
  const [fieldValues, setFieldValues] = useState(() => {
    if (value && typeof value === 'object' && value.fieldValues) {
      return value.fieldValues;
    }
    return {};
  });

  // Update parent when selection or field values change
  useEffect(() => {
    onChange({
      selectedOption,
      selectedLabel: dropdownOptions.find(o => o.id === selectedOption)?.label || '',
      fieldValues
    });
  }, [selectedOption, fieldValues]);

  const handleOptionChange = (optionId) => {
    setSelectedOption(optionId);
    // Initialize field values for this option if not exists
    if (!fieldValues[optionId]) {
      const subFields = conditions[optionId] || [];
      const initialValues = {};
      subFields.forEach(sf => {
        initialValues[sf.id] = '';
      });
      setFieldValues(prev => ({ ...prev, [optionId]: initialValues }));
    }
  };

  const handleFieldChange = (fieldId, fieldValue) => {
    setFieldValues(prev => ({
      ...prev,
      [selectedOption]: {
        ...(prev[selectedOption] || {}),
        [fieldId]: fieldValue
      }
    }));
  };

  const currentSubFields = selectedOption ? (conditions[selectedOption] || []) : [];
  const currentFieldValues = fieldValues[selectedOption] || {};

  const renderSubFieldInput = (subField) => {
    const val = currentFieldValues[subField.id] || '';
    
    switch (subField.type) {
      case 'date':
        return (
          <Input
            type="date"
            value={val}
            onChange={(e) => handleFieldChange(subField.id, e.target.value)}
            className="flex-1"
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={val}
            onChange={(e) => handleFieldChange(subField.id, e.target.value)}
            className="flex-1"
          />
        );
      case 'checkbox':
        return (
          <Checkbox
            checked={val === true || val === 'true'}
            onCheckedChange={(checked) => handleFieldChange(subField.id, checked)}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={val}
            onChange={(e) => handleFieldChange(subField.id, e.target.value)}
            placeholder={subField.name}
            className="flex-1"
          />
        );
    }
  };

  return (
    <div className="space-y-3">
      {/* Dropdown selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Select {field.name}</Label>
        <Select value={selectedOption} onValueChange={handleOptionChange}>
          <SelectTrigger className="w-full" data-testid={`conditional-dropdown-${field.fieldId}`}>
            <SelectValue placeholder={`Choose ${field.name.toLowerCase()}...`} />
          </SelectTrigger>
          <SelectContent>
            {dropdownOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Conditional sub-fields based on selection */}
      {selectedOption && currentSubFields.length > 0 && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center">
                <ChevronDown className="h-4 w-4 text-purple-700" />
              </div>
              <span className="text-sm font-medium text-purple-800">
                Additional details for "{dropdownOptions.find(o => o.id === selectedOption)?.label}"
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {currentSubFields.map((subField) => (
                <div key={subField.id} className="space-y-1">
                  <Label className="text-xs text-gray-600">
                    {subField.name}
                    {subField.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {renderSubFieldInput(subField)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!selectedOption && (
        <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed">
          <ChevronDown className="h-8 w-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            Select an option above to see additional fields
          </p>
        </div>
      )}
    </div>
  );
};

export default ConditionalField;
